<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { RouteLocationRaw } from 'vue-router';
import BaseIcon from '@/components/base/BaseIcon.vue';
import TruncatedText from '@/components/common/TruncatedText.vue';
import { formatTaxId, entityTypeFromTaxId } from '@/utils/formatters';
import { getEntityTypeIcon } from '@/utils/kyc';
import { entityRouteForTaxId, type EntitySection } from '@/utils/entityRoutes';

type EntityType = 'company' | 'person';

/**
 * The app's one entity-identity cell: a colored person/company badge, the entity name as the
 * primary line, and its tax id as a muted subtitle which is the link. When there's no name, the
 * tax id becomes the primary line and remains the link.
 *
 * Routing precedence: an explicit `to` wins; otherwise `section` builds the named route from the
 * tax id (`entityRouteForTaxId`); with neither, it links to the entity's home page. An unresolvable
 * tax id — or `link=false` — renders everything static.
 */
interface Props {
  taxId: string;
  name?: string | null;
  /** Overrides the tax-id-based type inference (for masked/partial ids). */
  type?: EntityType;
  /** Person route key when the CPF itself isn't it. */
  personUuid?: string | null;

  // Routing.
  section?: EntitySection;
  to?: RouteLocationRaw;
  link?: boolean;
  newTab?: boolean;

  // Presentation.
  showIcon?: boolean;
  /** `inline` keeps name and tax id on one line, for dense table rows. */
  size?: 'default' | 'compact' | 'inline';
  /** Shows a "not identified" note in place of an empty tax id instead of a blank line. */
  showMissingTaxId?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  link: true,
  showIcon: true,
  size: 'default',
});

const resolvedType = computed<EntityType>(() => props.type ?? entityTypeFromTaxId(props.taxId));
const entityIcon = computed(() => getEntityTypeIcon(resolvedType.value));
const isInline = computed(() => props.size === 'inline');
// `inline` shares compact's small type scale; it only differs in layout.
const isCompact = computed(() => props.size === 'compact' || isInline.value);

// The tax id is always the link; the name (when present) sits above it as the primary label.
const hasName = computed(() => !!props.name);
const isTaxIdMissing = computed(() => props.showMissingTaxId && !props.taxId);
const formattedTaxId = computed(() => (isTaxIdMissing.value ? 'Documento não identificado' : formatTaxId(props.taxId)));

const primaryTextClass = computed(() => (isCompact.value ? 'text-xs font-semibold' : 'text-sm font-semibold'));
// The tax id renders as a muted subtitle when a name is shown, or as the primary line when it isn't.
const taxIdTextClass = computed(() =>
  hasName.value
    ? [{ 'font-mono': !isTaxIdMissing.value }, isCompact.value ? 'text-[10px]' : 'text-[11px]']
    : [primaryTextClass.value],
);
const staticTaxIdColorClass = computed(() => {
  if (isTaxIdMissing.value) return 'text-surface-400';
  return hasName.value ? 'text-surface-500' : 'text-surface-900';
});

const routeTo = computed<RouteLocationRaw | null>(() => {
  if (props.link === false) return null;
  if (props.to) return props.to;
  return entityRouteForTaxId(props.taxId, props.section, {
    type: resolvedType.value,
    personUuid: props.personUuid,
  });
});
</script>

<template>
  <div class="flex min-w-0 gap-2" :class="isInline ? 'items-center' : 'items-start'">
    <div
      v-if="showIcon"
      v-tooltip.top="resolvedType === 'company' ? 'Empresa' : 'Pessoa'"
      class="flex shrink-0 items-center justify-center rounded-full"
      :class="[
        resolvedType === 'person' ? 'bg-blue-50 text-blue-600' : 'bg-amber-50 text-amber-600',
        isCompact ? 'h-5 w-5' : 'h-7 w-7',
        isInline ? '' : 'mt-0.5',
      ]"
    >
      <BaseIcon :class="isCompact ? 'text-[11px]' : 'text-sm'">{{ entityIcon }}</BaseIcon>
    </div>

    <div class="flex min-w-0" :class="isInline ? 'items-baseline gap-2' : 'flex-col'">
      <!-- The full name on hover only when it is cut. -->
      <TruncatedText
        v-if="hasName"
        :text="name"
        class="text-surface-900"
        :class="[primaryTextClass, isInline ? 'min-w-0' : '']"
      />

      <RouterLink
        v-if="routeTo"
        :to="routeTo"
        :target="newTab ? '_blank' : undefined"
        class="block truncate text-blue-600 hover:underline"
        :class="[taxIdTextClass, isInline ? 'shrink-0' : '']"
        @click.stop
      >{{ formattedTaxId }}</RouterLink>
      <span
        v-else
        class="block truncate"
        :class="[taxIdTextClass, staticTaxIdColorClass, isInline ? 'shrink-0' : '']"
      >{{ formattedTaxId }}</span>
    </div>
  </div>
</template>
